import React, { useState, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import Login from "./components/Login";
import ProtectedRoute from "./components/guards/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import Dashboard from "./components/Dashboard";
import AddAppModal from "./components/AddAppModal";
import Settings from "./components/Settings";
import ModuleLoader from "./components/ModuleLoader";
import TrainingDashboardApp from "@fleet-os/training-dashboard";
import { AuthGuard } from "@fleet-os/auth";
import { dashboards } from "@fleet-os/config";

// Lazy load the features
const ISDApp = React.lazy(() => import("@fleet-os/isd"));
const CSDApp = React.lazy(() => import("@fleet-os/csd"));
const DPTApp = React.lazy(() => import("@fleet-os/dpt"));
const CSMApp = React.lazy(() => import("@fleet-os/csm"));
const HawkeyeApp = React.lazy(() => import("@fleet-os/hawkeye"));
const EngBoardsApp = React.lazy(() => import("@fleet-os/eng-boards"));
const CityOpsApp = React.lazy(() => import("@fleet-os/city-ops"));
const ProcessGovernanceApp = React.lazy(() => import("@fleet-os/process-governance"));
const OpalApp = React.lazy(() => import("@fleet-os/opal"));
const AbsApp = React.lazy(() => import("@fleet-os/abs-dashboard"));
const VmsApp = React.lazy(() => import("@fleet-os/vms"));
const NinetyApp = React.lazy(() => import("@fleet-os/ninety"));

const featureMap = {
  isd: ISDApp,
  csd: CSDApp,
  dpt: DPTApp,
  csm: CSMApp,
  hawkeye: HawkeyeApp,
  boards: EngBoardsApp,
  "city-ops": CityOpsApp,
  "process-governance": ProcessGovernanceApp,
  opal: OpalApp,
  abs: AbsApp,
  vms: VmsApp,
  ninety: NinetyApp
};

import API_BASE_URL from "./config/api";
import "./App.css";

const INITIAL_APPS = [
  { id: "1", name: "My Jira", logo: "Jira", category: "Recent used", isFavorite: false },
  { id: "2", name: "Autocab", logo: "Autocab", category: "Recent used", isFavorite: false },
  { id: "3", name: "Drivers Por.", logo: "Drivers Portal", category: "Recent used", isFavorite: true },
  { id: "4", name: "Slack", logo: "Slack", category: "Recent used", isFavorite: true },
  { id: "5", name: "Nudg", logo: "Nudg", category: "Recent used", isFavorite: false },
  { id: "6", name: "Gmail", logo: "Gmail", category: "Work apps", isFavorite: false },
  { id: "7", name: "Google Dri.", logo: "Drive", category: "Work apps", isFavorite: false },
  { id: "8", name: "Slack", logo: "Slack", category: "Work apps", isFavorite: false },
  { id: "10", name: "Drivers Por.", logo: "Drivers Portal", category: "Work apps", isFavorite: false }
];

function App() {
  const { instance } = useMsal();
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("fleet_authenticated") === "true";
  });

  const [currentUser, setCurrentUser] = useState(() => {
    const saved = localStorage.getItem("fleet_user");
    return saved ? JSON.parse(saved) : null;
  });

  const [apps, setApps] = useState(INITIAL_APPS);
  const [categories, setCategories] = useState(["Recent used", "Favorites", "Work apps"]);
  const [activeCategory, setActiveCategory] = useState("Recent used");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [isAddAppOpen, setIsAddAppOpen] = useState(false);

  const handleLogin = (userObj) => {
    localStorage.setItem("fleet_authenticated", "true");
    if (userObj) {
      localStorage.setItem("fleet_user", JSON.stringify(userObj));
      setCurrentUser(userObj);
    }
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    localStorage.removeItem("fleet_authenticated");
    localStorage.removeItem("fleet_user");
    setCurrentUser(null);
    setIsAuthenticated(false);

    try {
      await fetch(`${API_BASE_URL}/auth/logout`, { method: "POST" });
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const handleCategoryClick = (cat) => {
    setActiveCategory(cat);
    const element = document.getElementById(`section-${cat.replace(/\s+/g, '-').toLowerCase()}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleToggleFavorite = (appId) => {
    setApps((prevApps) => prevApps.map((app) => app.id === appId ? { ...app, isFavorite: !app.isFavorite } : app));
  };

  const handleDeleteApp = (appId) => {
    setApps((prevApps) => prevApps.filter((app) => app.id !== appId));
  };

  const handleAddApp = (newAppData) => {
    const newApp = {
      id: Date.now().toString(),
      name: newAppData.name,
      logo: newAppData.logo,
      category: newAppData.category,
      isFavorite: newAppData.isFavorite
    };
    setApps((prevApps) => [...prevApps, newApp]);
    setIsAddAppOpen(false);
  };

  const handleAddSection = () => {
    const sectionName = prompt("Enter a name for the new Section / Category:");
    if (sectionName && sectionName.trim()) {
      const trimmedName = sectionName.trim();
      if (categories.some((c) => c.toLowerCase() === trimmedName.toLowerCase())) {
        alert("A category with this name already exists!");
        return;
      }
      setCategories((prevCategories) => [...prevCategories, trimmedName]);
    }
  };

  return (
    <div className="app">
      <div className="bg-mesh" />
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />
        } />

        <Route element={<ProtectedRoute />}>
          {dashboards.map((dash) => {
            const FeatureComponent = featureMap[dash.id];
            return (
              <Route key={dash.id} element={<AuthGuard moduleName={dash.name} />}>
                <Route path={`${dash.path}/*`} element={
                  <Suspense fallback={<ModuleLoader name={dash.name} />}>
                    {FeatureComponent ? <FeatureComponent /> : <div>Component not found</div>}
                  </Suspense>
                } />
              </Route>
            );
          })}

          {/* Training renders full-page (its own sidebar), like the other dashboards — not inside
              the shell's main-viewport. */}
          <Route path="/training/*" element={<TrainingDashboardApp />} />

          <Route path="/*" element={
            <div className="app-layout">
              <Sidebar
                categories={categories}
                activeCategory={activeCategory}
                onCategoryClick={handleCategoryClick}
                onAddAppClick={() => setIsAddAppOpen(true)}
                onAddSectionClick={handleAddSection}
                onLogout={handleLogout}
                userName={currentUser?.name || "User"}
                userRole={currentUser?.role || "Viewer"}
                userEmail={currentUser?.email || "user@example.com"}
              />
              <main className="main-viewport">
                <Routes>
                  <Route
                    path="/"
                    element={
                      <Dashboard
                        apps={apps}
                        categories={categories}
                        searchQuery={searchQuery}
                        onSearchChange={setSearchQuery}
                        viewMode={viewMode}
                        onToggleViewMode={setViewMode}
                        onToggleFavorite={handleToggleFavorite}
                        onDeleteApp={handleDeleteApp}
                        userName={currentUser?.name || "User"}
                      />
                    }
                  />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              <AddAppModal
                isOpen={isAddAppOpen}
                onClose={() => setIsAddAppOpen(false)}
                onSubmit={handleAddApp}
                categories={categories.filter((c) => c.toLowerCase() !== "favorites")}
              />
            </div>
          } />
        </Route>
        
        <Route path="*" element={<Navigate to={isAuthenticated ? "/" : "/login"} replace />} />
      </Routes>
    </div>
  );
}

export default App;
